/**
 * The effects panel. §6.4.
 *
 * Laid out as Canva lays out its own, because that is the one customers know:
 * a grid of tiles, each drawn with the real effect on the real object; the
 * chosen tile's controls open directly beneath its row; bending text and the
 * advanced looks below; and one button at the foot to take it all off.
 *
 * One effect at a time, plus a curve or a line for text - choosing a second
 * effect replaces the first. Every combination the panel can make is one that
 * has been checked to print.
 */

import { useEffect, useMemo } from 'react';
import type { Colour, Effect, EffectKind, ShapeLayer, TextLayer } from '../model/types';
import { isGradient } from '../model/types';
import { useSelectedLayers } from '../state/store';
import { CONTROLS, controlLabel, effectsFor, startingEffect } from '../effects/plan';
import { PRESETS, intensityOf, presetById, type Preset } from '../effects/presets';
import { applyPreset, offPath, setCurve, setEffect, textOnPath } from '../state/actions';
import { fontFamilies, preload } from '../text/fonts';
import { EffectTile } from './EffectTile';
import { Slider } from './Slider';
import { ColourField } from './ColourField';

const NAMES: Record<EffectKind, string> = {
  drop: 'Drop',
  glow: 'Glow',
  echo: 'Echo',
  outline: 'Outline',
  background: 'Background',
  splice: 'Splice',
  hollow: 'Hollow',
  neon: 'Neon',
  glitch: 'Glitch',
};

export function EffectsPanel( { onClose }: { onClose: () => void } ) {
  const selected = useSelectedLayers();

  // A text and a drawing chosen together is the one two-object case: the
  // panel offers to put the text on the line.
  const text = selected.find( ( l ): l is TextLayer => l.type === 'text' ) ?? null;
  const line = selected.find( ( l ): l is ShapeLayer => l.type === 'shape' && l.shape === 'free' ) ?? null;
  const pairing = selected.length === 2 && text && line;

  const layer = selected.length === 1 && ( selected[ 0 ]?.type === 'text' || selected[ 0 ]?.type === 'shape' )
    ? ( selected[ 0 ] as TextLayer | ShapeLayer )
    : null;

  return (
    <aside className="tb-side tb-effects" aria-label="Effects">
      <div className="tb-side__head">
        <h2 className="tb-side__title">Effects</h2>
        <button type="button" className="tb-btn tb-btn--quiet tb-btn--icon" aria-label="Close effects" onClick={ onClose }>
          ✕
        </button>
      </div>

      { pairing && text && line && (
        <div className="tb-effects__pair">
          <p>Lay the text along the line you drew.</p>
          <button type="button" className="tb-btn tb-btn--primary" onClick={ () => textOnPath( text.id, line.id ) }>
            Put text on the line
          </button>
        </div>
      ) }

      { ! layer && ! pairing && (
        <p className="tb-side__empty">
          Select a text or a shape
          <br />
          to give it an effect.
        </p>
      ) }

      { layer && <Effects layer={ layer } /> }
    </aside>
  );
}

function Effects( { layer }: { layer: TextLayer | ShapeLayer } ) {
  const effect = layer.effect ?? null;
  const preset = presetById( effect?.preset );
  const own = ownColour( layer );
  const kinds = effectsFor( layer );

  // Presets whose font the shop has. The rest are not offered: a look that
  // falls back to another font is not the look on the tile.
  const families = fontFamilies();
  const presets = useMemo( () => PRESETS.filter( ( p ) => families.includes( p.font.family ) ), [ families.join( '|' ) ] );

  // The advanced tiles draw in their own fonts, which have to be fetched.
  useEffect( () => {
    if ( layer.type !== 'text' ) return;

    void preload( presets.map( ( p ) => ( { family: p.font.family, weight: p.font.weight, style: 'normal' as const } ) ) );
  }, [ layer.type, presets ] );

  const choose = ( kind: EffectKind ) => {
    // Choosing the effect already on is not a reset: its settings stay.
    if ( effect && effect.kind === kind && ! effect.preset ) return;

    setEffect( layer.id, startingEffect( kind, own ) );
  };

  const rows = chunk( kinds, 3 );

  return (
    <div className="tb-effects__body">
      <section className="tb-effects__section">
        <h3 className="tb-effects__heading">Effects</h3>

        { rows.map( ( row ) => (
          <div key={ row.join() } className="tb-effects__group">
            <div className="tb-tiles">
              { row.map( ( kind ) => (
                <EffectTile
                  key={ kind }
                  layer={ layer }
                  label={ NAMES[ kind ] }
                  effect={ startingEffect( kind, own ) }
                  selected={ !! effect && effect.kind === kind && ! preset }
                  onClick={ () => choose( kind ) }
                />
              ) ) }
            </div>

            { effect && ! preset && row.includes( effect.kind ) && <Controls layer={ layer } effect={ effect } /> }
          </div>
        ) ) }
      </section>

      { layer.type === 'text' && <Bending layer={ layer } /> }

      { layer.type === 'text' && presets.length > 0 && (
        <section className="tb-effects__section">
          <h3 className="tb-effects__heading">Advanced</h3>

          { chunk( presets, 3 ).map( ( row ) => (
            <div key={ row.map( ( p ) => p.id ).join() } className="tb-effects__group">
              <div className="tb-tiles">
                { row.map( ( p ) => (
                  <EffectTile
                    key={ p.id }
                    layer={ layer }
                    label={ p.name }
                    effect={ p.effect( 50 ) }
                    font={ p.font }
                    fill={ p.fill }
                    sample={ p.upper ? 'AG' : 'Ag' }
                    selected={ preset?.id === p.id }
                    title={ `${ p.name } - sets the font to ${ p.font.family }` }
                    onClick={ () => {
                      if ( preset?.id !== p.id ) applyPreset( layer.id, p );
                    } }
                  />
                ) ) }
              </div>

              { preset && effect && row.some( ( p ) => p.id === preset.id ) && (
                <Intensity layer={ layer } preset={ preset } effect={ effect } />
              ) }
            </div>
          ) ) }
        </section>
      ) }

      { effect && (
        <div className="tb-effects__foot">
          <button type="button" className="tb-btn tb-btn--primary tb-effects__remove" onClick={ () => setEffect( layer.id, null ) }>
            Remove effect
          </button>
        </div>
      ) }
    </div>
  );
}

/** The chosen effect's own controls, as its kind defines them. */
function Controls( { layer, effect }: { layer: TextLayer | ShapeLayer; effect: Effect } ) {
  const set = <K extends keyof Effect>( key: K, value: Effect[ K ] ) =>
    setEffect( layer.id, { ...effect, [ key ]: value }, `effect:${ layer.id }:${ String( key ) }` );

  return (
    <div className="tb-effects__controls">
      { CONTROLS[ effect.kind ].map( ( control ) => {
        if ( control === 'colour' || control === 'colour2' ) {
          const value = ( control === 'colour' ? effect.colour : effect.colour2 ) ?? effect.colour;

          return (
            <ColourField
              key={ control }
              label={ controlLabel( effect.kind, control ) }
              value={ value }
              onChange={ ( colour ) => set( control, colour ) }
            />
          );
        }

        return (
          <Slider
            key={ control }
            label={ controlLabel( effect.kind, control ) }
            value={ effect[ control ] }
            min={ control === 'direction' ? -180 : 0 }
            max={ control === 'direction' ? 180 : 100 }
            onChange={ ( value ) => set( control, value ) }
          />
        );
      } ) }
    </div>
  );
}

/** A preset's one control. */
function Intensity( { layer, preset, effect }: { layer: TextLayer | ShapeLayer; preset: Preset; effect: Effect } ) {
  return (
    <div className="tb-effects__controls">
      <Slider
        label="Intensity"
        value={ intensityOf( preset, effect ) }
        onChange={ ( value ) => setEffect( layer.id, preset.effect( value ), `effect:${ layer.id }:intensity` ) }
      />
    </div>
  );
}

/**
 * Bending text: straight, on an arc, or along a drawn line.
 *
 * A line cannot be chosen from here alone - there has to be one - so its tile
 * explains how, and the panel offers the real button when a text and a drawing
 * are selected together.
 */
function Bending( { layer }: { layer: TextLayer } ) {
  const curve = layer.curve ?? 0;
  const onLine = !! layer.path;
  const bent = Math.abs( curve ) >= 1;

  return (
    <section className="tb-effects__section">
      <h3 className="tb-effects__heading">Shape</h3>

      <div className="tb-effects__group">
        <div className="tb-tiles">
          <EffectTile
            layer={ layer }
            label="Straight"
            effect={ null }
            sample="ABCD"
            selected={ ! bent && ! onLine }
            onClick={ () => {
              if ( onLine ) offPath( layer.id );
              else if ( bent ) setCurve( layer.id, 0 );
            } }
          />
          <EffectTile
            layer={ layer }
            label="Curve"
            effect={ null }
            curve={ 60 }
            sample="ABCD"
            selected={ bent && ! onLine }
            disabled={ onLine }
            title={ onLine ? 'Take the text off its line first' : 'Bend the text along an arc' }
            onClick={ () => {
              if ( ! bent ) setCurve( layer.id, 50 );
            } }
          />
          <EffectTile
            layer={ layer }
            label="Along a line"
            effect={ null }
            wave
            sample="ABCDE"
            selected={ onLine }
            title="Draw a line with the pen or pencil, then Shift-click it and this text together"
            onClick={ () => undefined }
          />
        </div>

        { bent && ! onLine && (
          <div className="tb-effects__controls">
            <Slider
              label="Curve"
              value={ curve }
              min={ -100 }
              max={ 100 }
              // Dragged to nothing, it is straight text again - and the tiles say so.
              onChange={ ( value ) => setCurve( layer.id, value, `curve:${ layer.id }` ) }
            />
          </div>
        ) }

        { onLine && (
          <div className="tb-effects__controls">
            <p className="tb-effects__hint">The text follows its line. Drag the text box to move both.</p>
            <button type="button" className="tb-btn" onClick={ () => offPath( layer.id ) }>
              Take off the line
            </button>
          </div>
        ) }

        { ! onLine && ! bent && (
          <p className="tb-effects__hint">
            To run text along a line, draw one with the pen or pencil, then Shift-click it and this text together.
          </p>
        ) }
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------ */

function chunk<T>( items: T[], size: number ): T[][] {
  const out: T[][] = [];

  for ( let i = 0; i < items.length; i += size ) out.push( items.slice( i, i + size ) );

  return out;
}

/** The object's own colour, which several effects start from. */
function ownColour( layer: TextLayer | ShapeLayer ): Colour {
  if ( layer.type === 'text' ) return isGradient( layer.fill ) ? layer.fill.stops[ 0 ]?.colour ?? { space: 'cmyk', value: [ 0, 0, 0, 1 ] } : layer.fill;

  const fill = layer.fill;

  if ( isGradient( fill ) ) return fill.stops[ 0 ]?.colour ?? { space: 'cmyk', value: [ 0, 0, 0, 1 ] };

  // A line's colour is its stroke.
  return layer.stroke && layer.shape === 'line' ? layer.stroke.colour : fill;
}

/** An effect's name, for the toolbar's "Effects: Glow". */
export const effectNames = NAMES;
